import { readMultimodalReferenceTokens } from '@/ai/references/multimodal-reference-placeholder';
import type { WorkspaceReferenceImage } from '../contracts/runtime-state';
import { findWorkspaceImageMentionQuery } from '../shared/workspace-image-mentions';
import {
  type WorkspaceMentionReference,
  buildWorkspaceMentionReferences,
} from './workspace-reference-mention';

export type WorkspaceMentionRange = {
  start: number;
  end: number;
  query: string;
};

// An invisible caret landing point immediately after a noneditable chip.
// It never enters Prompt state or the model payload.
export const WORKSPACE_MENTION_CARET_ANCHOR = '\u2060';

const INVISIBLE_EDITOR_CHARS = /[\u200b\u2060\ufeff]/g;
const BLOCK_TAGS = new Set(['DIV', 'P']);

function normalizedText(text: string) {
  return text
    .replace(INVISIBLE_EDITOR_CHARS, '')
    .replace(/\u00a0/g, ' ')
    .replace(/\r\n?/g, '\n');
}

function isElement(node: Node): node is HTMLElement {
  return node.nodeType === Node.ELEMENT_NODE && node instanceof HTMLElement;
}

function isPlaceholderBreak(node: HTMLElement) {
  return (
    BLOCK_TAGS.has(node.tagName) &&
    node.childNodes.length === 1 &&
    isElement(node.firstChild!) &&
    node.firstChild.tagName === 'BR'
  );
}

function serializeNode(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) {
    return normalizedText(node.textContent ?? '');
  }
  if (!isElement(node)) return '';

  const token = node.dataset.workspaceImageMention;
  if (token) return token;
  if (node.tagName === 'BR') {
    return node === node.parentNode?.lastChild ? '' : '\n';
  }
  return serializeChildren(node);
}

function serializeChildren(parent: Node): string {
  let text = '';
  for (const child of Array.from(parent.childNodes)) {
    if (isElement(child) && BLOCK_TAGS.has(child.tagName)) {
      if (isPlaceholderBreak(child) && child === parent.lastChild) continue;
      const part = isPlaceholderBreak(child) ? '' : serializeChildren(child);
      if (text && !text.endsWith('\n')) text += '\n';
      text += part;
    } else {
      text += serializeNode(child);
    }
  }
  return text;
}

export function readWorkspaceMentionEditorText(editor: HTMLElement) {
  return serializeChildren(editor);
}

function readPrefixOffset(
  editor: HTMLElement,
  container: Node,
  offset: number
) {
  const prefix = document.createRange();
  prefix.selectNodeContents(editor);
  prefix.setEnd(container, offset);
  return serializeChildren(prefix.cloneContents()).length;
}

export function readWorkspaceMentionSelection(editor: HTMLElement) {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return null;
  const range = selection.getRangeAt(0);

  if (
    !editor.contains(range.startContainer) ||
    !editor.contains(range.endContainer)
  ) {
    return null;
  }
  return {
    start: readPrefixOffset(editor, range.startContainer, range.startOffset),
    end: readPrefixOffset(editor, range.endContainer, range.endOffset),
  };
}

function rawTextOffset(text: string, visibleOffset: number) {
  let offset = 0;
  let visible = 0;
  while (offset < text.length) {
    const character = text[offset]!;
    if (
      character === '\u200b' ||
      character === WORKSPACE_MENTION_CARET_ANCHOR ||
      character === '\ufeff'
    ) {
      offset += 1;
      continue;
    }
    if (visible >= visibleOffset) break;
    offset += 1;
    visible += 1;
  }
  return offset;
}

function setSelection(range: Range) {
  range.collapse(true);
  const selection = window.getSelection();
  selection?.removeAllRanges();
  selection?.addRange(range);
}

export function setWorkspaceMentionCaret(
  editor: HTMLElement,
  requestedOffset: number
) {
  const offset = Math.max(0, requestedOffset);
  const range = document.createRange();
  let consumed = 0;
  const children = Array.from(editor.childNodes);

  for (const [index, node] of children.entries()) {
    const length = serializeNode(node).length;

    if (node.nodeType === Node.TEXT_NODE && offset <= consumed + length) {
      range.setStart(
        node,
        rawTextOffset(node.textContent ?? '', offset - consumed)
      );
      setSelection(range);
      return;
    }

    if (isElement(node) && node.dataset.workspaceImageMention) {
      if (offset <= consumed + length) {
        if (offset === consumed) {
          range.setStart(editor, index);
        } else {
          const following = children[index + 1];
          if (
            following?.nodeType === Node.TEXT_NODE &&
            following.textContent?.startsWith(WORKSPACE_MENTION_CARET_ANCHOR)
          ) {
            range.setStart(following, 1);
          } else {
            range.setStart(editor, index + 1);
          }
        }
        setSelection(range);
        return;
      }
    } else if (isElement(node) && offset <= consumed + length) {
      const firstChild = node.firstChild;
      if (firstChild?.nodeType === Node.TEXT_NODE) {
        range.setStart(
          firstChild,
          rawTextOffset(firstChild.textContent ?? '', offset - consumed)
        );
      } else {
        range.setStart(editor, index + 1);
      }
      setSelection(range);
      return;
    }
    consumed += length;
  }

  range.selectNodeContents(editor);
  range.collapse(false);
  setSelection(range);
}

function createPromptTextSegment(text: string) {
  const segment = document.createElement('span');
  segment.className =
    'cms-block-workspace-workspace-1-workspace-image-mention__contentText';
  segment.textContent = text;
  return segment;
}

// Native image elements should survive text edits and the Polish typewriter
// sequence. Recreating them for every character reloads their preview URLs.
const detachedChipsByEditor = new WeakMap<HTMLElement, HTMLElement[]>();
const MAX_DETACHED_CHIPS = 64;

function createReferenceChip(
  reference: WorkspaceMentionReference,
  availableChips: HTMLElement[]
) {
  const reusableIndex = availableChips.findIndex(
    (chip) =>
      chip.dataset.workspaceImageReferenceId === reference.id &&
      chip.dataset.workspaceImagePreviewUrl === (reference.previewUrl ?? '') &&
      chip.dataset.workspaceReferenceKind === reference.kind
  );
  const chip =
    reusableIndex >= 0
      ? availableChips.splice(reusableIndex, 1)[0]!
      : document.createElement('span');

  chip.className =
    'cms-block-workspace-workspace-1-workspace-image-mention__chip';
  chip.contentEditable = 'false';
  chip.dataset.workspaceImageMention = reference.token;
  chip.dataset.workspaceImageMentionIndex = String(reference.kindIndex + 1);
  chip.dataset.workspaceImageReferenceId = reference.id;
  chip.dataset.workspaceImagePreviewUrl = reference.previewUrl ?? '';
  chip.dataset.workspaceReferenceKind = reference.kind;
  chip.setAttribute('aria-label', reference.label);

  if (reusableIndex >= 0) {
    const label = chip.querySelector<HTMLElement>(
      '.cms-block-workspace-workspace-1-workspace-image-mention__chipLabel'
    );
    if (label) label.textContent = reference.label;
    return chip;
  }

  if (reference.kind === 'image' && reference.previewUrl) {
    const thumbnail = document.createElement('img');
    thumbnail.className =
      'cms-block-workspace-workspace-1-workspace-image-mention__chipImage';
    thumbnail.src = reference.previewUrl;
    thumbnail.alt = '';
    thumbnail.setAttribute('aria-hidden', 'true');
    chip.appendChild(thumbnail);
  }

  const label = document.createElement('span');
  label.className =
    'cms-block-workspace-workspace-1-workspace-image-mention__chipLabel';
  label.textContent = reference.label;

  chip.appendChild(label);
  return chip;
}

function appendPromptContent(
  fragment: DocumentFragment,
  prompt: string,
  references: WorkspaceMentionReference[],
  availableChips: HTMLElement[]
) {
  let cursor = 0;
  for (const match of readMultimodalReferenceTokens(prompt)) {
    if (match.start > cursor) {
      fragment.append(
        createPromptTextSegment(prompt.slice(cursor, match.start))
      );
    }
    const reference = references.find(
      (item) => item.kind === match.kind && item.kindIndex === match.number - 1
    );
    if (reference) {
      fragment.append(
        createReferenceChip(reference, availableChips),
        document.createTextNode(WORKSPACE_MENTION_CARET_ANCHOR)
      );
    } else {
      fragment.append(createPromptTextSegment(match.token));
    }
    cursor = match.end;
  }
  if (cursor < prompt.length) {
    fragment.append(createPromptTextSegment(prompt.slice(cursor)));
  }
}

/** Renders all token positions from the canonical Prompt string in one pass. */
export function renderWorkspaceMentionEditor(params: {
  editor: HTMLElement;
  prompt: string;
  images?: WorkspaceReferenceImage[];
  references?: WorkspaceMentionReference[];
  activeRange?: WorkspaceMentionRange | null;
  caretOffset?: number | null;
}) {
  const references =
    params.references ??
    buildWorkspaceMentionReferences({
      images: params.images ?? [],
      media: [],
    });
  const fragment = document.createDocumentFragment();
  const previous = Array.from(
    params.editor.querySelectorAll<HTMLElement>(
      '[data-workspace-image-mention]'
    )
  );
  const availableChips = [
    ...previous,
    ...(detachedChipsByEditor.get(params.editor) ?? []),
  ];
  const range = params.activeRange;
  if (range) {
    appendPromptContent(
      fragment,
      params.prompt.slice(0, range.start),
      references,
      availableChips
    );
    const activeQuery = document.createElement('span');
    activeQuery.className =
      'cms-block-workspace-workspace-1-workspace-image-mention__activeQuery';
    activeQuery.textContent = params.prompt.slice(range.start, range.end);
    fragment.append(activeQuery);
    appendPromptContent(
      fragment,
      params.prompt.slice(range.end),
      references,
      availableChips
    );
  } else {
    appendPromptContent(fragment, params.prompt, references, availableChips);
  }

  // Keep only previews still present in the reference list, including
  // transiently hidden cards while Polish animates the rewritten text.
  const validImageSources = new Set(
    references.map((reference) =>
      JSON.stringify([reference.id, reference.previewUrl ?? '', reference.kind])
    )
  );
  detachedChipsByEditor.set(
    params.editor,
    availableChips
      .filter((chip) =>
        validImageSources.has(
          JSON.stringify([
            chip.dataset.workspaceImageReferenceId,
            chip.dataset.workspaceImagePreviewUrl,
            chip.dataset.workspaceReferenceKind ?? 'image',
          ])
        )
      )
      .slice(-MAX_DETACHED_CHIPS)
  );

  params.editor.replaceChildren(fragment);
  if (typeof params.caretOffset === 'number') {
    setWorkspaceMentionCaret(params.editor, params.caretOffset);
  }
}

export function getWorkspaceMentionChipRanges(editor: HTMLElement) {
  return Array.from(
    editor.querySelectorAll<HTMLElement>('[data-workspace-image-mention]')
  ).map((chip) => {
    const start = readPrefixOffset(
      editor,
      chip.parentNode!,
      Array.prototype.indexOf.call(chip.parentNode!.childNodes, chip)
    );
    return {
      start,
      end: start + (chip.dataset.workspaceImageMention?.length ?? 0),
    };
  });
}

export function findWorkspaceEditableMentionQuery(
  editor: HTMLElement,
  prompt: string,
  caretOffset: number
) {
  const candidate = findWorkspaceImageMentionQuery(prompt, caretOffset);
  if (!candidate) return null;

  const overlapsChip = getWorkspaceMentionChipRanges(editor).some(
    (chip) => candidate.start < chip.end && candidate.end > chip.start
  );
  return overlapsChip ? null : candidate;
}

export function findAdjacentWorkspaceMentionChip(
  editor: HTMLElement,
  offset: number,
  direction: 'backward' | 'forward'
) {
  return (
    getWorkspaceMentionChipRanges(editor).find((chip) =>
      direction === 'backward' ? chip.end === offset : chip.start === offset
    ) ?? null
  );
}
